// Org slug extraction from hostname for subdomain-based multi-tenancy.
//
// Production: <slug>.verso-cloud.io → slug = "lyonscomm"
// Dev:        localhost:5173        → slug = null (no subdomain routing)
//
// The slug is sent to POST /auth/session so the API can validate that the
// authenticated user actually belongs to the org matching the subdomain.

const PRODUCT_DOMAIN = import.meta.env.VITE_PRODUCT_DOMAIN || 'verso-cloud.io';

// Reserved slugs — infrastructure subdomains that cannot be used as org slugs.
// SYNC: this same list is duplicated in
//   cloud/workers/src/lib/validate.js
//   cloud/admin/workers/src/routes/organizations.js
// (pnpm workspace boundaries prevent sharing; keep the three copies in sync.)
const RESERVED_SLUGS = new Set([
  'admin', 'api', 'www', 'app', 'mail', 'ftp',
  'staging', 'dev', 'test', 'status', 'docs',
]);

/**
 * Extract the org slug from the current hostname.
 * Returns null in dev (localhost) or if hostname doesn't match the product domain.
 * @param {string} [hostname] - Override hostname for testing.
 */
export function getOrgSlug(hostname) {
  hostname = hostname || window.location.hostname;

  // Dev: localhost or LAN IP — no subdomain routing.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
    return null;
  }

  // Must be a subdomain of the product domain: <slug>.verso-cloud.io
  if (!hostname.endsWith(`.${PRODUCT_DOMAIN}`)) {
    return null;
  }

  const slug = hostname.slice(0, -(PRODUCT_DOMAIN.length + 1));

  // Must be a single label (no nested subdomains like a.b.verso-cloud.io).
  if (!slug || slug.includes('.')) {
    return null;
  }

  // Reserved slugs are not org subdomains — admin.verso-cloud.io is the admin
  // console, not an org named "admin".
  if (RESERVED_SLUGS.has(slug)) {
    return null;
  }

  return slug;
}

/**
 * Check if a slug is reserved (for org creation validation on the client side).
 */
export function isReservedSlug(slug) {
  return RESERVED_SLUGS.has(slug);
}

export { RESERVED_SLUGS };
